import axios from "@/axios";

const state = {
  stickerPacks: [],
  purchasedPacks: [],
  loading: false,
  error: null,
};

const getters = {
  stickerPacks: (state) => state.stickerPacks,
  isPackPurchased: (state) => (packId) => state.purchasedPacks.includes(packId),
};

const actions = {
  async fetchStickerPacks({ commit }) {
    commit("SET_LOADING", true);
    try {
      const response = await axios.get("sticker-packs");
      if (response.data.status === "OK") {
        const packs = response.data.payload;
        commit("SET_STICKER_PACKS", packs);

        // Extract purchased pack IDs
        const purchasedPackIds = packs
          .filter((pack) => pack.is_purchased)
          .map((pack) => pack.id);

        commit("SET_PURCHASED_PACKS", purchasedPackIds);
      }
    } catch (error) {
      commit("SET_ERROR", error.message);
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async buyStickerPack({ commit, dispatch }, packId) {
    commit("SET_LOADING", true);
    try {
      const response = await axios.post("sticker-packs/buy", {
        sticker_pack_id: packId,
      });

      if (response.data.status === "OK") {
        commit("ADD_PURCHASED_PACK", packId);
        commit("user/SET_BALANCE", response.data.payload.balance, {
          root: true,
        });

        dispatch(
          "app/showNotification",
          { text: response.data.payload.message, ok: true },
          { root: true }
        );

        return Promise.resolve();
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to purchase sticker pack";
      dispatch(
        "app/showNotification",
        { text: errorMessage, ok: true },
        { root: true }
      );
      commit("SET_ERROR", errorMessage);
      return Promise.reject(errorMessage);
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async giftStickerPack(
    { commit, dispatch },
    { packId, topicId, companionName }
  ) {
    commit("SET_LOADING", true);
    try {
      const response = await axios.post("sticker-packs/gift", {
        sticker_pack_id: packId,
        topic_id: topicId,
      });

      if (response.data.status === "OK") {
        commit("user/SET_BALANCE", response.data.payload.balance, {
          root: true,
        });

        dispatch(
          "app/showNotification",
          {
            text: `You've sent a sticker pack as a gift to ${companionName}!`,
            ok: true,
          },
          { root: true }
        );

        return Promise.resolve();
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to send gift";
      dispatch(
        "app/showNotification",
        { text: errorMessage, ok: true },
        { root: true }
      );
      commit("SET_ERROR", errorMessage);
      return Promise.reject(errorMessage);
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async sendSticker({ commit, dispatch }, { stickerId, topicId }) {
    commit("SET_LOADING", true);
    try {
      const response = await axios.post("stickers/send", {
        sticker_id: stickerId,
        topic_id: topicId,
      });

      if (response.data.status === "OK") {
        return Promise.resolve(response.data.payload.sticker_message);
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to send sticker";
      dispatch(
        "app/showNotification",
        { text: errorMessage, ok: true },
        { root: true }
      );
      commit("SET_ERROR", errorMessage);
      return Promise.reject(errorMessage);
    } finally {
      commit("SET_LOADING", false);
    }
  },
};

const mutations = {
  SET_STICKER_PACKS(state, packs) {
    state.stickerPacks = packs;
  },
  SET_PURCHASED_PACKS(state, packIds) {
    state.purchasedPacks = packIds;
  },
  ADD_PURCHASED_PACK(state, packId) {
    if (!state.purchasedPacks.includes(packId)) {
      state.purchasedPacks.push(packId);
    }
  },
  SET_LOADING(state, isLoading) {
    state.loading = isLoading;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
